import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Alert,
  Button,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { getAgents, getAgentStays, deleteAgentStay, getAgentPayments, deleteAgentPayment } from "./api/agentsApi";
import AgentFormDialog from "./AgentFormDialog";
import ConnectionDialog from "./ConnectionDialog";
import AgentPaymentDialog from "./AgentPaymentDialog";
import StatementDialog from "./StatementDialog";
import InvoiceDialog from "./InvoiceDialog";
import GroupConnectionDialog from "./GroupConnectionDialog";

const money = (n) => (n == null ? "—" : `£${Number(n).toFixed(2)}`);

function commissionLabel(commission, rate) {
  const type = String(commission || "NONE").toUpperCase();
  if (type === "NONE") return "No commission";
  return `${type} ${rate != null ? `${rate}%` : ""}`.trim();
}

// Balance highlight: red = student still owes, green = fully paid (£0).
// Explicit false only — a null/undefined flag stays unhighlighted.
function balanceBg(outstanding) {
  if (outstanding === true) return "#ffe6e6";
  if (outstanding === false) return "#e6ffe6";
  return undefined;
}
function balanceTitle(outstanding) {
  if (outstanding === true) return "Student has an outstanding balance";
  if (outstanding === false) return "Fully paid";
  return undefined;
}

// Commission we owe the agent for a stay: GROSS → rate × commissionable; else £0.
// Same rule the statement uses; prefers a backend value if one is ever supplied.
function commissionDue(s) {
  if (s?.commissionPayable != null) return Number(s.commissionPayable);
  return String(s?.commission || "").toUpperCase() === "GROSS"
    ? ((Number(s.commissionRate) || 0) / 100) * (Number(s.commissionableTotal) || 0)
    : 0;
}

export default function AgentsPage({ year }) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [dialog, setDialog] = useState({ open: false, mode: "create" });
  const [connDialog, setConnDialog] = useState({ open: false, mode: "add", connection: null });

  const [stays, setStays] = useState([]);
  const [staysLoading, setStaysLoading] = useState(false);
  const [staysError, setStaysError] = useState(null);

  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [payDialog, setPayDialog] = useState({ open: false, payment: null });
  const [statementOpen, setStatementOpen] = useState(false);
  const [invoiceStay, setInvoiceStay] = useState(null);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);

  const loadAgents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAgents();
      setAgents(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e);
      setAgents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  const selectedAgent = useMemo(
    () => agents.find((a) => String(a.id) === String(selectedId)) || null,
    [agents, selectedId]
  );

  const loadStays = useCallback(async (agentId) => {
    if (!agentId) {
      setStays([]);
      return;
    }
    setStaysLoading(true);
    setStaysError(null);
    try {
      const data = await getAgentStays(agentId);
      setStays(Array.isArray(data) ? data : []);
    } catch (e) {
      setStaysError(e);
      setStays([]);
    } finally {
      setStaysLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStays(selectedId);
  }, [selectedId, loadStays]);

  const loadPayments = useCallback(async (agentId) => {
    if (!agentId) {
      setPayments([]);
      return;
    }
    setPaymentsLoading(true);
    try {
      const data = await getAgentPayments(agentId);
      setPayments(Array.isArray(data) ? data : []);
    } catch {
      setPayments([]);
    } finally {
      setPaymentsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPayments(selectedId);
  }, [selectedId, loadPayments]);

  // Headline for the selected agent: what we still owe them across all linked
  // stays — Σ commission due (GROSS stays) minus Σ commission already paid.
  const totalCommissionPayable = useMemo(
    () => stays.reduce((acc, s) => acc + commissionDue(s), 0),
    [stays]
  );
  const totalCommissionPaid = useMemo(
    () => payments.reduce((acc, p) => acc + (Number(p.paymentAmount) || 0), 0),
    [payments]
  );
  const outstandingCommission = totalCommissionPayable - totalCommissionPaid;

  const handleSaved = useCallback(
    async (saved) => {
      await loadAgents();
      if (saved?.id != null) setSelectedId(saved.id);
    },
    [loadAgents]
  );

  const handleRemoveConnection = useCallback(
    async (s) => {
      if (!selectedId || !s?.stayId) return;
      if (!window.confirm(`Remove ${s.studentName || "this student"}'s link to this agent?`)) return;
      try {
        await deleteAgentStay(selectedId, s.stayId);
        await loadStays(selectedId);
      } catch (e) {
        window.alert(String(e.message || e));
      }
    },
    [selectedId, loadStays]
  );

  const handleDeletePayment = useCallback(
    async (p) => {
      if (!selectedId || !p?.paymentId) return;
      if (!window.confirm(`Delete this ${money(p.paymentAmount)} payment?`)) return;
      try {
        await deleteAgentPayment(selectedId, p.paymentId);
        await loadPayments(selectedId);
      } catch (e) {
        window.alert(String(e.message || e));
      }
    },
    [selectedId, loadPayments]
  );

  const filteredAgents = useMemo(() => {
    // Adult-less rows are excluded server-side (getAgents ?withAdult=true).
    const q = query.trim().toLowerCase();
    const list = q
      ? agents.filter((a) =>
          [a.name, a.email, a.phone]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q))
        )
      : agents;
    return list
      .slice()
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
  }, [agents, query]);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={800}>
          Agents
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setDialog({ open: true, mode: "create" })}
        >
          New agent
        </Button>
      </Stack>

      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
        {/* Agent list */}
        <Paper variant="outlined" sx={{ p: 2, width: 340, flexShrink: 0 }}>
          <TextField
            size="small"
            fullWidth
            label="Search agents"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ mb: 1 }}
          />
          {loading ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading agents…</Typography>
            </Stack>
          ) : error ? (
            <Alert severity="error">{String(error.message || error)}</Alert>
          ) : filteredAgents.length === 0 ? (
            <Typography variant="body2" sx={{ opacity: 0.7, py: 1 }}>
              {query.trim() ? "No agents match." : "No agents yet."}
            </Typography>
          ) : (
            <List dense disablePadding sx={{ maxHeight: 520, overflow: "auto" }}>
              {filteredAgents.map((a) => (
                <ListItemButton
                  key={a.id}
                  selected={String(a.id) === String(selectedId)}
                  onClick={() => setSelectedId(a.id)}
                >
                  <ListItemText
                    primary={a.name || `Agent #${a.id} (no adult)`}
                    secondary={commissionLabel(a.commission, a.commissionRate)}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Paper>

        {/* Selected agent detail */}
        <Paper variant="outlined" sx={{ p: 2, flex: "1 1 480px", minWidth: 0 }}>
          {!selectedAgent ? (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Select an agent to see their details and linked stays.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="h6" fontWeight={800}>
                  {selectedAgent.name || `Agent #${selectedAgent.id}`}
                </Typography>
                <Chip
                  size="small"
                  label={commissionLabel(selectedAgent.commission, selectedAgent.commissionRate)}
                />
                {selectedAgent.englishSpeaking ? (
                  <Chip size="small" variant="outlined" label="English-speaking" />
                ) : null}
                <Button
                  size="small"
                  startIcon={<EditIcon fontSize="small" />}
                  onClick={() => setDialog({ open: true, mode: "edit" })}
                >
                  Edit
                </Button>
                <Button size="small" variant="outlined" onClick={() => setStatementOpen(true)}>
                  Statement
                </Button>

                <Box sx={{ flexGrow: 1 }} />

                <Stack alignItems="flex-end">
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    Outstanding commission
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight={800}
                    sx={{ lineHeight: 1, color: outstandingCommission > 0.005 ? "#1a1f71" : "success.main" }}
                  >
                    {money(outstandingCommission)}
                  </Typography>
                </Stack>
              </Stack>

              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {[selectedAgent.email, selectedAgent.phone].filter(Boolean).join(" · ") || "No contact details"}
              </Typography>
              {selectedAgent.notes ? (
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  {selectedAgent.notes}
                </Typography>
              ) : null}

              <Divider />

              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ flexGrow: 1 }}>
                  Linked stays
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={() => setConnDialog({ open: true, mode: "add", connection: null })}
                >
                  Add connection
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={() => setGroupDialogOpen(true)}
                >
                  Add by group
                </Button>
              </Stack>

              {staysLoading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography variant="body2">Loading stays…</Typography>
                </Stack>
              ) : staysError ? (
                <Alert severity="error">{String(staysError.message || staysError)}</Alert>
              ) : stays.length === 0 ? (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  No students linked to this agent yet.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Student</TableCell>
                      <TableCell>Stay</TableCell>
                      <TableCell>Campus</TableCell>
                      <TableCell>Commission</TableCell>
                      <TableCell align="right">Commissionable</TableCell>
                      <TableCell align="right">Non-comm.</TableCell>
                      <TableCell align="right">Comm. due</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stays.map((s) => (
                      <TableRow
                        key={s.linkId ?? `${s.stayId}-${s.studentId}`}
                        sx={{ bgcolor: balanceBg(s.outstandingBalance) }}
                        title={balanceTitle(s.outstandingBalance)}
                      >
                        <TableCell>{s.studentName || `#${s.studentId}`}</TableCell>
                        <TableCell>
                          {s.arrivalDate} – {s.departureDate}
                        </TableCell>
                        <TableCell>{s.campus}</TableCell>
                        <TableCell>{commissionLabel(s.commission, s.commissionRate)}</TableCell>
                        <TableCell align="right">{money(s.commissionableTotal)}</TableCell>
                        <TableCell align="right">{money(s.nonCommissionableTotal)}</TableCell>
                        <TableCell align="right">{money(commissionDue(s))}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <IconButton
                            size="small"
                            aria-label="Invoice"
                            onClick={() => setInvoiceStay(s)}
                          >
                            <ReceiptLongIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label="Edit connection"
                            onClick={() => setConnDialog({ open: true, mode: "edit", connection: s })}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            aria-label="Remove connection"
                            onClick={() => handleRemoveConnection(s)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              <Divider />

              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ flexGrow: 1 }}>
                  Commission payments
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={() => setPayDialog({ open: true, payment: null })}
                >
                  Record payment
                </Button>
              </Stack>

              {paymentsLoading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography variant="body2">Loading payments…</Typography>
                </Stack>
              ) : payments.length === 0 ? (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  No commission payments recorded.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Account</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map((p) => (
                      <TableRow key={p.paymentId}>
                        <TableCell>{p.paymentDate}</TableCell>
                        <TableCell>{p.paymentAccount}</TableCell>
                        <TableCell align="right">{money(p.paymentAmount)}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <IconButton
                            size="small"
                            aria-label="Edit payment"
                            onClick={() => setPayDialog({ open: true, payment: p })}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            aria-label="Delete payment"
                            onClick={() => handleDeletePayment(p)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Stack>
          )}
        </Paper>
      </Box>

      <AgentFormDialog
        open={dialog.open}
        mode={dialog.mode}
        agent={dialog.mode === "edit" ? selectedAgent : null}
        onClose={() => setDialog((d) => ({ ...d, open: false }))}
        onSaved={handleSaved}
      />

      <ConnectionDialog
        open={connDialog.open}
        mode={connDialog.mode}
        agent={selectedAgent}
        connection={connDialog.connection}
        year={year}
        existingStayIds={stays.map((s) => s.stayId)}
        onClose={() => setConnDialog((d) => ({ ...d, open: false }))}
        onSaved={() => loadStays(selectedId)}
      />

      <AgentPaymentDialog
        open={payDialog.open}
        agentId={selectedId}
        payment={payDialog.payment}
        onClose={() => setPayDialog({ open: false, payment: null })}
        onSaved={() => loadPayments(selectedId)}
      />

      <GroupConnectionDialog
        open={groupDialogOpen}
        agent={selectedAgent}
        existingStayIds={stays.map((s) => s.stayId)}
        onClose={() => setGroupDialogOpen(false)}
        onSaved={() => loadStays(selectedId)}
      />

      <StatementDialog
        open={statementOpen}
        agent={selectedAgent}
        onClose={() => setStatementOpen(false)}
      />

      <InvoiceDialog
        open={!!invoiceStay}
        agent={selectedAgent}
        stay={invoiceStay}
        onClose={() => setInvoiceStay(null)}
      />
    </Box>
  );
}
